import * as L from 'leaflet';
import 'leaflet.markercluster';
import { getMarkerPopUpTemplate, getThemePath, markerHasPopUp } from './helper';

const DEFAULT_ZOOM = 3;
/**
 * @return L.map https://leafletjs.com/reference-1.7.1.html#map
 */
export function initMap() {
    let map = L.map('leafletMap', {
        center: [47, -40],
        zoom: DEFAULT_ZOOM,
        scrollWheelZoom: false, // empeche de scroller avec la souris
        maxZoom: 7,
    });

    L.tileLayer(
        'https://{s}.tile.thunderforest.com/outdoors/{z}/{x}/{y}.png?apikey=a7774dda178d416daf6c2ce040469d3a',
        {
            attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }
    ).addTo(map);

    // Defini l'emplacement des boutons de zoom sur la carte
    map.zoomControl.setPosition('topleft');

    return map;
}

/**
 * @param {string} cssClasses Une liste de classes séparées par des espaces à ajouter au cluster.
 * @return {L.MarkerClusterGroup} https://github.com/Leaflet/Leaflet.markercluster
 */
export function initCluster(cssClasses) {
    if (cssClasses === undefined) {
        cssClasses = '';
    }

    return new L.MarkerClusterGroup({
        showCoverageOnHover: false,
        iconCreateFunction(cluster) {
            let childCount = cluster.getChildCount();
            let className = `${cssClasses} marker-cluster-`;

            if (childCount < 25) {
                className += 'small';
            } else if (childCount < 80) {
                className += 'medium';
            } else {
                className += 'large';
            }

            if (childCount === 1) {
                className += ' marker-cluster-single';
            }

            let typeClassName = getCustomCSSClassesParType(cluster);
            className += ` ${typeClassName}`;

            return L.divIcon({
                html:
                    childCount > 1
                        ? `<div><span>${childCount}</span></div>`
                        : `<div></div>`,
                className,
                iconSize: null,
            });
        },
        singleMarkerMode: true,
    });
}

/**
 * Ajoute une classe css custom en fonction du type du marker.
 *
 * @param {L.MarkerCluster} cluster
 * @return {string}
 */
function getCustomCSSClassesParType(cluster) {
    /** @type @type {Map<string, string>} Les différents types de markers */
    let types = new Map();
    types.set('biomethane', 0);
    types.set('biogaz', 0);
    types.set('vcool', 0);
    types.set('autoconso', 0);
    types.set('siège social', 0);

    // On compte le nombre total de marqueur par type et par cluster
    cluster.getAllChildMarkers().forEach((marker) => {
        types.set(marker.options.type, types.get(marker.options.type) + 1);
    });

    let typeClassName = '';
    /** @type {Map<string, string>} La correspondance entre type de marqueur et nom de classe CSS */
    let classMapping = new Map();
    classMapping.set('biomethane', 'marker-cluster-biomethane');
    classMapping.set('biogaz', 'marker-cluster-biogaz');
    classMapping.set('vcool', 'marker-cluster-vcool');
    classMapping.set('autoconso', 'marker-cluster-autoconso');
    classMapping.set('siège social', 'marker-cluster-siegeSocial');
    let numberOfTypes = 0;

    for (const type of types.keys()) {
        if (types.get(type) > 0) {
            typeClassName += ` ${classMapping.get(type)}`;
        }
        // Le premier élément de `typeClassName` sera toujours un élément vide, on ne le compte donc pas dans le total
        // des types.
        numberOfTypes = typeClassName.split(' ').length - 1;
    }
    if (numberOfTypes === 1) {
        typeClassName += ' marker-cluster-single-type';
    }

    return typeClassName;
}

/**
 * @param {array} data, point à afficher sur la carte, fournis via la single page en php
 * @return {array} array de L.marker L.marker https://leafletjs.com/reference-1.7.1.html#marker
 */
export function createMarker(data) {
    let markers = [];

    data.forEach((item) => {
        if (item.latitude === null || item.longitude === null) {
            return;
        }

        // verifie si le marker aura une popup lorsqu'on clique dessus
        let hasPopUp = markerHasPopUp(item);
        let iconClassName = hasPopUp === false ? 'no-popup' : '';

        // Crée un marker
        let marker = L.marker([Number(item.latitude), Number(item.longitude)], {
            icon: getIcon(item.type, iconClassName),
            latitude: Number(item.latitude),
            longitude: Number(item.longitude),
            id: item.id,
            nom: item.nom,
            type: item.type,
            site: item.site,
            adresse: item.adresse,
            code_postal: item.code_postal,
            ville: item.ville,
            image: item.image,
        });

        if (hasPopUp === true) {
            marker.bindPopup(getMarkerPopUpTemplate(item));
        }

        markers.push(marker);
    });

    return markers;
}

/**
 * @param {*string} type, biogaz, biométhane etc, la couleur de l'icone change selon le type
 * @param {string} className, la class css de l'icone
 * @return L.icon https://leafletjs.com/reference-1.7.1.html#icon
 */
function getIcon(type, className) {
    return L.icon({
        className,
        iconUrl: `${getThemePath()}/img/pictos/${getIconByType(type)}`,
        iconSize: [50, 45],
        iconAnchor: [0, 45],
        popupAnchor: [27, -22],
    });
}

/**
 * Récupère un marker celon son type, chaque icone change de couleur
 * @param {*string} type
 * @return {string}
 */
function getIconByType(type) {
    type = type.toLowerCase();

    if (type === 'siège social' || type === 'siege social') {
        return 'marker01.svg';
    }

    if (type === 'biomethane') {
        return 'marker03.svg';
    }

    if (type === 'vcool') {
        return 'marker04.svg';
    }

    if (type === 'autoconso') {
        return 'marker05.svg';
    }

    // Retourner un marker par defaut
    return 'marker02.svg'; // Biogaze
}

/**
 *
 * @param {L.map} map https://leafletjs.com/reference-1.7.1.html#map
 * @param {object} geolocalisation {latitude: float, longitude: float}
 * @return {void}
 */
export function zoomMap(map, geolocalisation) {
    map.setView(
        [geolocalisation.latitude, geolocalisation.longitude],
        getZoom(map),
        {
            animate: true,
            pan: { duration: 0.5 },
        }
    );
}

/**
 * Defini le zoom à adapté pour sur la carte
 * Empeche d'avoir un zoom moins puissant que celui actuel qui donnerer l'effet de dézoomer
 * @param {L.map} map https://leafletjs.com/reference-1.7.1.html#map
 */
export function getZoom(map) {
    let zoom = 9;
    // Si le zoom actuel est plus grand que le zoom qu'on veux atteindre
    if (map.getZoom() > zoom) {
        return map.getZoom(); // Empeche de dezoomer
    }

    return zoom;
}
